import { type ParsedUrlQuery } from 'querystring';

import { isVakanties } from '@vakantiesnl/components/src/utils';

import { initialFilterState } from './config';
import {
	getThemes,
	getPage,
	getMinPrice,
	getMaxPrice,
	getAccoTypes,
	getMealplans,
	getDepartureDate,
	getFlexibleDepartureDate,
	getSorting,
	getAwards,
	getDistances,
	getFacilities,
	getRating,
	getDeals,
	getCampaignAccoTypes,
	getCampaignDepartureDate,
	getAirport,
	getTransport,
	getChainsParamsFromQuery,
	getTourOperatorsParamsFromQuery,
	getStars,
	getCampaignAttributes,
	getCampaignMaxPrice,
	getCampaignMinPrice,
	getAirlines,
	getFlightProvider,
	getAttributes,
} from './getFilterStateHelpers';
import { type FilterState } from './interfaces';
import type { SelectedGeo } from '../../hooks/queries/geo/interfaces';
import { getCampaignFromQuery, parseQueryParam } from '../../util/queryHelpers';

export function getFilterState(query: ParsedUrlQuery, selectedGeo: SelectedGeo): FilterState {
	const filterState: FilterState = { ...initialFilterState };

	const { countries, regions, cities } = selectedGeo;

	if (!!countries.length) {
		filterState.countrySlugs = countries.map((c) => c.slug);
		filterState.countries = countries.map((c) => ({ entityId: c.entityId, slug: c.slug }));
	}

	const shouldAllowRegionFilter = filterState.countries?.length;
	if (shouldAllowRegionFilter) {
		filterState.regionSlugs = regions.map((r) => r.slug);
		filterState.regions = regions.map((r) => ({ entityId: r.entityId, slug: r.slug }));
	}

	const shouldAllowCityFilter = filterState.regions?.length > 0 && filterState.countries.length === 1;
	if (shouldAllowCityFilter) {
		filterState.citySlugs = cities.map((c) => c.slug);
		filterState.cities = cities.map((city) => ({ entityId: city.entityId, slug: city.slug }));
	}

	const themes = getThemes(query);
	if (themes) {
		filterState.themes = themes;
	}

	const page = getPage(query);
	if (page) {
		filterState.page = page;
	}

	const minPrice = getMinPrice(query);
	if (minPrice) {
		filterState.priceMin = minPrice;
	}

	const maxPrice = getMaxPrice(query);
	if (maxPrice) {
		filterState.priceMax = maxPrice;
	}

	const accoTypes = getAccoTypes(query);
	if (accoTypes) {
		filterState.accoType = accoTypes;
	}

	const mealplans = getMealplans(query);
	if (mealplans) {
		filterState.mealplans = mealplans;
	}

	const airports = getAirport(query);
	if (airports) {
		filterState.departureAirport = airports;
	}

	const departureDate = getDepartureDate(query);
	if (departureDate) {
		filterState.departureDate = departureDate;
	}

	const flexibleDepartureDate = getFlexibleDepartureDate(query);
	if (flexibleDepartureDate && departureDate && departureDate.length === 1) {
		filterState.flexibleDepartureDate = flexibleDepartureDate;
	}

	const awards = getAwards(query);
	if (awards) {
		filterState.awards = awards;
	}

	const distances = getDistances(query);
	if (distances) {
		filterState.distances = distances;
	}

	const facilities = getFacilities(query);
	if (facilities) {
		filterState.facilities = facilities;
	}

	const rating = getRating(query);
	filterState.rating = rating || '';

	const stars = getStars(query);
	if (stars) {
		filterState.stars = stars;
	}

	const isDealsRoute = getDeals(query);
	filterState.isDealsRoute = isDealsRoute || false;

	const transport = getTransport(query);
	if (transport) {
		filterState.transport = transport;
	}

	const sort = getSorting(query);
	if (sort) {
		filterState.sort = sort;
	}

	const attributes = getAttributes(query);
	if (!!attributes?.length) {
		filterState.attributes = attributes;
	}

	const campaign = getCampaignFromQuery(query);

	// Anything in the query takes priority over the campaign config (same as campaign geo, which is handled in another fn: getGeoParamsFromQuery)
	if (campaign) {
		filterState.campaign = campaign.slug;

		// Only set accoTypes from campaign when they were not already part of the query
		if (!filterState.accoType.length) {
			const campaignAccoTypes = getCampaignAccoTypes(campaign);

			if (campaignAccoTypes) {
				filterState.accoType = campaignAccoTypes;
			}
		}

		// Only set departure from campaign when they were not already part of the query
		const campaignDepartureDate = getCampaignDepartureDate(campaign);

		if (!filterState.departureDate?.length && campaignDepartureDate?.length) {
			filterState.departureDate = campaignDepartureDate;
		}

		const mealplans = parseQueryParam(campaign.mealplans);

		if (mealplans && !filterState.mealplans?.length) {
			filterState.mealplans = mealplans;
		}

		const campaignAttributes = getCampaignAttributes(campaign);

		if (!filterState.attributes.length && campaignAttributes?.length) {
			filterState.attributes = campaignAttributes;
		}

		const campaignMinPrice = getCampaignMinPrice(campaign);

		if (campaignMinPrice && !filterState.priceMin) {
			filterState.priceMin = campaignMinPrice;
		}

		const campaignMaxPrice = getCampaignMaxPrice(campaign);

		if (campaignMaxPrice && !filterState.priceMax) {
			filterState.priceMax = campaignMaxPrice;
		}
	}

	const chains = getChainsParamsFromQuery(query);
	if (!!chains) {
		filterState.chains = chains;
		filterState.chainsSlugs = chains.map((chain) => chain.slug);
	}

	const tourOperators = getTourOperatorsParamsFromQuery(query);
	if (!!tourOperators) {
		filterState.tourOperators = tourOperators;
		filterState.tourOperatorsSlugs = tourOperators.map((tourOperator) => tourOperator.slug);
	}

	const airlines = getAirlines(query);
	if (!!airlines) {
		filterState.airlines = airlines;
	}

	const flightProvider = getFlightProvider(query);
	if (!!flightProvider) {
		filterState.flightProvider = flightProvider;
	}

	/** Always set INCLUDING_FLIGHT for vakanties */
	if (isVakanties) {
		filterState.transport = 'INCLUDING_FLIGHT';
	}

	return filterState;
}
